/**
 * feixing.js — 三元紫白九宫飞星（独立排盘事实模块）。
 *
 * 这里不向 analyze()/verdict()/timing.js 输入任何数据，也不解释吉凶。
 * 年、月起法依《钦定协纪辨方书》卷八“三元年九星入中宫”、
 * “三元月九星入中宫”：
 * https://zh.wikisource.org/zh-hant/欽定協紀辨方書_(四庫全書本)/卷08
 *
 * 原文的关键区分是：入中之年星逐年逆转；得到中宫星后，八宫顺布。
 * 月星同理：逐月逆转中宫星，再顺布九宫。
 */

import { fourPillars, fromJD, termsOfYear, toJD, toJDN } from './ganzhi.js';
import { nextGanzhiDay } from './date-candidates.js';

export const PALACE_PATH = Object.freeze(['中', '乾', '兑', '艮', '离', '坎', '坤', '震', '巽']);

/** 洛书本位：四巽、九离、二坤／三震、五中、七兑／八艮、一坎、六乾。 */
export const LUOSHU = Object.freeze({
  巽: 4, 离: 9, 坤: 2,
  震: 3, 中: 5, 兑: 7,
  艮: 8, 坎: 1, 乾: 6
});

export const FLYING_STARS = Object.freeze({
  1: Object.freeze({ name: '贪狼', label: '一白水', color: '白', element: '水' }),
  2: Object.freeze({ name: '巨门', label: '二黑土', color: '黑', element: '土' }),
  3: Object.freeze({ name: '禄存', label: '三碧木', color: '碧', element: '木' }),
  4: Object.freeze({ name: '文曲', label: '四绿木', color: '绿', element: '木' }),
  5: Object.freeze({ name: '廉贞', label: '五黄土', color: '黄', element: '土' }),
  6: Object.freeze({ name: '武曲', label: '六白金', color: '白', element: '金' }),
  7: Object.freeze({ name: '破军', label: '七赤金', color: '赤', element: '金' }),
  8: Object.freeze({ name: '左辅', label: '八白土', color: '白', element: '土' }),
  9: Object.freeze({ name: '右弼', label: '九紫火', color: '紫', element: '火' })
});

const mod = (value, divisor) => ((value % divisor) + divisor) % divisor;
export const shiftStar = (star, steps) => mod(star - 1 + steps, 9) + 1;

/** 把入中星按中→乾→兑→艮→离→坎→坤→震→巽飞布。 */
export function flyPalaces(center, dun = '阳遁') {
  if (!Number.isInteger(center) || center < 1 || center > 9) {
    throw new TypeError('center 必须是 1–9 的整数');
  }
  if (dun !== '阳遁' && dun !== '阴遁') throw new TypeError("dun 只能是 '阳遁' 或 '阴遁'");
  const step = dun === '阳遁' ? 1 : -1;
  return Object.fromEntries(PALACE_PATH.map((palace, index) => [palace, shiftStar(center, step * index)]));
}

/** 1864 上元甲子一白入中，之后逐年退一星。 */
export function annualCenter(solarYear) {
  if (!Number.isInteger(solarYear)) throw new TypeError('solarYear 必须是整年');
  return shiftStar(1, -(solarYear - 1864));
}

/** 三元九运：1864 起一运，每运 20 年，每元 60 年，180 年一周。 */
export function yuanYun(solarYear) {
  const cycleYear = mod(solarYear - 1864, 180);
  return {
    yuan: cycleYear < 60 ? '上元' : cycleYear < 120 ? '中元' : '下元',
    yun: Math.floor(cycleYear / 20) + 1
  };
}

const MONTH_ORDER = Object.freeze(['寅', '卯', '辰', '巳', '午', '未', '申', '酉', '戌', '亥', '子', '丑']);
const YEAR_GROUP_START = Object.freeze({
  子: 8, 午: 8, 卯: 8, 酉: 8,
  辰: 5, 戌: 5, 丑: 5, 未: 5,
  寅: 2, 申: 2, 巳: 2, 亥: 2
});

/** 月建以寅为正月；中宫星逐月退一，宫位内仍顺布。 */
export function monthlyCenter(yearBranch, monthBranch) {
  const start = YEAR_GROUP_START[yearBranch];
  const order = MONTH_ORDER.indexOf(monthBranch);
  if (!start || order < 0) throw new TypeError('年支或月建不合法');
  return shiftStar(start, -order);
}

function solarYearOf(at, pillars) {
  const currentGregorianIndex = mod(at.year - 1984, 60);
  return pillars.year.index === currentGregorianIndex ? at.year : at.year - 1;
}

const withoutCenter = (layout) => Object.fromEntries(
  Object.entries(layout).filter(([palace]) => palace !== '中')
);

/**
 * 日紫白采用 A 说：冬至后第一个甲子日一白入中、顺飞；
 * 夏至后第一个甲子日九紫入中、逆飞。二至当天若为甲子，
 * 当天即起。在交节后、首个甲子前的数日，按新遁以该甲子锚点向前反推。
 *
 * 这是工程选择，非古法定论；“最近甲子”、“至前甲子”、“至后遇闰另算”
 * 均有流传，但暂无能压倒其他三家的古籍依据。详见 docs/feixing-sources.md。
 */
export const DAY_FLYING_STAR_STATUS = Object.freeze({
  implemented: true,
  school: 'A-冬至后第一个甲子',
  caveat: '四家说法无定论，此为工程选择'
});

const SOLSTICE_NAMES = new Set(['冬至', '夏至']);
const dateObject = ({ year, month, day }) => ({ year, month, day });

function dateFromString(value) {
  const [year, month, day] = value.split('-').map(Number);
  return { year, month, day };
}

function activeSolstice(at, tzOffset) {
  const currentJD = toJD(at.year, at.month, at.day, at.hour ?? 12, at.minute ?? 0);
  const terms = [];
  for (let year = at.year - 1; year <= at.year; year += 1) {
    terms.push(...termsOfYear(year, tzOffset, { includeSolstices: true })
      .filter(({ name }) => SOLSTICE_NAMES.has(name)));
  }
  const active = terms
    .filter(({ jd }) => jd <= currentJD)
    .sort((a, b) => b.jd - a.jd)[0];
  if (!active) throw new RangeError('无法定位当前时刻之前的冬至/夏至');
  return active;
}

function firstJiaziOnOrAfter(term, calendarOptions) {
  const moment = fromJD(term.jd);
  const termDate = dateObject(moment);
  const termDay = fourPillars({ ...termDate, hour: 12, minute: 0 }, calendarOptions);
  if (termDay.day.name === '甲子') return termDate;
  return dateFromString(nextGanzhiDay('甲子', termDay).date);
}

/** 按 A 说计算日盘；只返回排盘事实，不解释吉凶。 */
export function dailyFlyingStar(at, options = {}) {
  const tzOffset = options.tzOffset ?? 8;
  const term = activeSolstice(at, tzOffset);
  const anchorDate = firstJiaziOnOrAfter(term, options);
  const daysFromAnchor = toJDN(at.year, at.month, at.day)
    - toJDN(anchorDate.year, anchorDate.month, anchorDate.day);
  const isYang = term.name === '冬至';
  const dun = isYang ? '阳遁' : '阴遁';
  const center = shiftStar(isYang ? 1 : 9, isYang ? daysFromAnchor : -daysFromAnchor);
  const layout = flyPalaces(center, dun);
  return {
    center,
    palaces: withoutCenter(layout),
    dun,
    anchor: {
      date: anchorDate,
      ganzhi: '甲子',
      term: term.name
    },
    daysFromAnchor,
    school: DAY_FLYING_STAR_STATUS.school,
    caveat: DAY_FLYING_STAR_STATUS.caveat
  };
}

/** 返回年、月、日三盘与供渲染消费的合并视图。 */
export function feixing(at) {
  const pillars = fourPillars(at);
  const solarYear = solarYearOf(at, pillars);
  const period = yuanYun(solarYear);

  const yearCenter = annualCenter(solarYear);
  const yearLayout = flyPalaces(yearCenter, '阳遁');
  const monthCenter = monthlyCenter(pillars.year.branch, pillars.month.branch);
  const monthLayout = flyPalaces(monthCenter, '阳遁');
  const day = dailyFlyingStar(at, pillars.calendarOptions);

  const stars = Object.fromEntries(PALACE_PATH.map((palace) => [palace, {
    year: yearLayout[palace],
    month: monthLayout[palace],
    day: palace === '中' ? day.center : day.palaces[palace]
  }]));

  return {
    year: {
      center: yearCenter,
      palaces: withoutCenter(yearLayout),
      ...period,
      solarYear,
      direction: '顺飞'
    },
    month: {
      center: monthCenter,
      palaces: withoutCenter(monthLayout),
      monthBranch: pillars.month.branch,
      direction: '顺飞'
    },
    day,
    stars
  };
}
